import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X, SlidersHorizontal, PackageSearch, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import ProductCard from "@/components/shop/ProductCard";
import CampaignBanner from "@/components/CampaignBanner";

const SORTS = [
  ["featured", "Featured"],
  ["newest", "Newest"],
  ["price_asc", "Price: Low to High"],
  ["price_desc", "Price: High to Low"],
  ["rating", "Best Rated"],
];

function FilterPanel({ filters, setFilters, categories, brands }) {
  return (
    <div className="space-y-7" data-testid="filter-panel">
      <div>
        <h4 className="mb-3 text-[11px] font-bold uppercase tracking-[0.2em] text-3">Price Range</h4>
        <div className="flex items-center gap-2">
          <input
            data-testid="filter-min-price"
            type="number"
            min="0"
            placeholder="Min"
            value={filters.min_price}
            onChange={(e) => setFilters((f) => ({ ...f, min_price: e.target.value }))}
            className="h-10 w-full rounded-lg border border-line bg-input px-3 text-sm text-1 placeholder:text-[var(--pq-text3)] focus:border-amber-500/60 focus:outline-none"
          />
          <span className="text-3">–</span>
          <input
            data-testid="filter-max-price"
            type="number"
            min="0"
            placeholder="Max"
            value={filters.max_price}
            onChange={(e) => setFilters((f) => ({ ...f, max_price: e.target.value }))}
            className="h-10 w-full rounded-lg border border-line bg-input px-3 text-sm text-1 placeholder:text-[var(--pq-text3)] focus:border-amber-500/60 focus:outline-none"
          />
        </div>
      </div>
      <div>
        <h4 className="mb-3 text-[11px] font-bold uppercase tracking-[0.2em] text-3">Availability</h4>
        {[["", "All"], ["in", "In Stock"], ["out", "Out of Stock"]].map(([val, label]) => (
          <label key={val} className="mb-2 flex cursor-pointer items-center gap-2.5 text-sm text-2">
            <input
              type="radio"
              name="availability"
              data-testid={`filter-availability-${val || "all"}`}
              checked={filters.availability === val}
              onChange={() => setFilters((f) => ({ ...f, availability: val }))}
              className="h-4 w-4 accent-amber-500"
            />
            {label}
          </label>
        ))}
      </div>
      <div>
        <h4 className="mb-3 text-[11px] font-bold uppercase tracking-[0.2em] text-3">Rating</h4>
        <div className="flex gap-2">
          {[["0", "Any"], ["4", "4+"], ["4.5", "4.5+"]].map(([val, label]) => (
            <button
              key={val}
              data-testid={`filter-rating-${val}`}
              onClick={() => setFilters((f) => ({ ...f, min_rating: val }))}
              className={`rounded-lg border px-3.5 py-2 text-xs font-bold transition-colors ${
                filters.min_rating === val
                  ? "border-amber-500 bg-amber-500/10 text-amber-400"
                  : "border-line text-2 hoverable-strong"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <div>
        <h4 className="mb-3 text-[11px] font-bold uppercase tracking-[0.2em] text-3">Category</h4>
        <div className="flex flex-wrap gap-2">
          {categories.map((c) => (
            <button
              key={c.id}
              data-testid={`filter-category-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              onClick={() => setFilters((f) => ({ ...f, category: f.category === c.name ? "" : c.name, subcategory: "", flag: "" }))}
              className={`flex items-center gap-1.5 rounded-full border py-1.5 text-xs font-bold transition-colors ${
                filters.category === c.name
                  ? "border-amber-500 bg-amber-500/10 text-amber-400"
                  : "border-line text-2 hoverable-strong"
              } ${c.image ? "pl-1.5 pr-3.5" : "px-3.5"}`}
            >
              {c.image && (
                <img src={c.image} alt="" className="h-5 w-5 shrink-0 rounded-full border border-line bg-white object-contain" />
              )}
              {c.name}
            </button>
          ))}
        </div>
        {(categories.find((c) => c.name === filters.category)?.subcategories || []).length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2 border-l-2 border-amber-500/20 pl-3" data-testid="filter-subcategories">
            {categories.find((c) => c.name === filters.category).subcategories.map((s) => (
              <button
                key={s.id}
                data-testid={`filter-subcategory-${s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onClick={() => setFilters((f) => ({ ...f, subcategory: f.subcategory === s.name ? "" : s.name }))}
                className={`rounded-full border px-3 py-1 text-[11px] font-bold transition-colors ${
                  filters.subcategory === s.name
                    ? "border-amber-500 bg-amber-500/10 text-amber-400"
                    : "border-line text-3 hoverable-strong"
                }`}
              >
                {s.name}
              </button>
            ))}
          </div>
        )}
      </div>
      {brands.length > 0 && (
        <div>
          <h4 className="mb-3 text-[11px] font-bold uppercase tracking-[0.2em] text-3">Brand</h4>
          <div className="flex flex-wrap gap-2">
            {brands.map((b) => (
              <button
                key={b.id}
                data-testid={`filter-brand-${(b.slug || b.id).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onClick={() => setFilters((f) => ({ ...f, brand: f.brand === b.id ? "" : b.id }))}
                className={`rounded-full border px-3.5 py-1.5 text-xs font-bold transition-colors ${
                  filters.brand === b.id
                    ? "border-amber-500 bg-amber-500/10 text-amber-400"
                    : "border-line text-2 hoverable-strong"
                }`}
              >
                {b.name}
              </button>
            ))}
          </div>
        </div>
      )}
      <button
        data-testid="filter-clear"
        onClick={() => setFilters({ category: "", subcategory: "", flag: "", brand: "", min_price: "", max_price: "", availability: "", min_rating: "0" })}
        className="text-xs font-bold uppercase tracking-[0.15em] text-3 transition-colors hover:text-amber-400"
      >
        Clear all filters
      </button>
    </div>
  );
}

export default function ShopPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get("q") || "");
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [sort, setSort] = useState("featured");
  const [filters, setFilters] = useState({
    category: searchParams.get("category") || "",
    subcategory: searchParams.get("subcategory") || "",
    flag: searchParams.get("flag") || "",
    brand: searchParams.get("brand") || "",
    bulk: searchParams.get("bulk") || "",
    offers: searchParams.get("offers") || "",
    ids: searchParams.get("ids") || "",
    min_price: "",
    max_price: "",
    availability: "",
    min_rating: "0",
  });
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const searchRef = useRef(null);
  const searchInputRef = useRef(null);
  const suggestSeq = useRef(0);
  const productsSeq = useRef(0);

  // Deep link from the mobile bottom nav — focus the search box on arrival.
  useEffect(() => {
    if (searchParams.get("focus") === "search") {
      window.scrollTo({ top: 0 });
      const t = setTimeout(() => searchInputRef.current?.focus(), 350);
      return () => clearTimeout(t);
    }
  }, [searchParams]);

  useEffect(() => {
    api.get("/shop/categories").then(({ data }) => setCategories(data.categories || data)).catch(() => {});
    api.get("/shop/brands").then(({ data }) => setBrands((data.brands || []).filter((b) => b.count > 0))).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (search.trim().length < 2) {
      setSuggestions([]);
      return;
    }
    const t = setTimeout(() => {
      const seq = ++suggestSeq.current;
      api.get("/shop/suggestions", { params: { q: search } })
        .then(({ data }) => { if (seq === suggestSeq.current) setSuggestions(data); })  // drop stale responses
        .catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const fetchProducts = useCallback(async (pageNum, append) => {
    if (append) setLoadingMore(true);
    else setLoading(true);
    setError("");
    const seq = ++productsSeq.current;  // stale-response guard: only the newest request may write results
    const clientFilter = filters.bulk || filters.offers; // preset rails filter client-side (small catalogue)
    try {
      const params = {
        q: debouncedSearch,
        sort,
        page: pageNum,
        limit: clientFilter ? 48 : 12,
        category: filters.category,
        subcategory: filters.subcategory,
        flag: filters.flag,
        brand: filters.brand,
        availability: filters.availability,
        min_rating: filters.min_rating,
        ids: filters.ids,
      };
      if (filters.min_price) params.min_price = filters.min_price;
      if (filters.max_price) params.max_price = filters.max_price;
      const { data } = await api.get("/shop/products", { params });
      if (seq !== productsSeq.current) return;  // a newer request is in flight — discard
      let list = data.products;
      if (filters.bulk) list = list.filter((p) => (p.min_order_qty || 1) > 1);
      if (filters.offers) list = list.filter((p) => (p.discount_pct || 0) > 0);
      setProducts((prev) => (append ? [...prev, ...list] : list));
      setTotal(clientFilter ? (append ? list.length : list.length) : data.total);
      setPage(data.page);
      setPages(clientFilter ? 1 : data.pages);
    } catch {
      if (seq === productsSeq.current) setError("Something went wrong. Please try again.");
    } finally {
      if (seq === productsSeq.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [debouncedSearch, sort, filters]);

  useEffect(() => {
    fetchProducts(1, false);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (debouncedSearch) next.set("q", debouncedSearch);
      else next.delete("q");
      return next;
    }, { replace: true });
  }, [fetchProducts]); // eslint-disable-line react-hooks/exhaustive-deps

  const chipDefs = [
    { label: "All Products", category: "", flag: "" },
    ...categories.map((c) => ({ label: c.name, category: c.name, flag: "" })),
    { label: "New Arrivals", category: "", flag: "new_arrival" },
  ];

  const activeChip = chipDefs.findIndex((c) => c.category === filters.category && c.flag === filters.flag);
  const activeCategory = categories.find((c) => c.name === filters.category);

  return (
    <div className="min-h-screen pb-24 pt-28" data-testid="shop-page">
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}>
          <p className="mb-3 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.25em] text-amber-500">
            <span className="h-px w-10 bg-amber-500" />
            Direct Sourcing & Supply
          </p>
          <h1 className="font-display text-4xl font-semibold tracking-tight text-1 sm:text-5xl">Shop <span className="text-amber-500">PAQVERO</span></h1>
          <p className="mt-3 text-base text-2 lg:text-lg">
            Packaging, shipping supplies and business essentials — sourced directly and stocked by PAQVERO.
          </p>
        </motion.div>

        <div className="mt-6">
          <CampaignBanner />
        </div>

        {activeCategory && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="mt-6 flex items-center gap-6 overflow-hidden rounded-2xl border border-line bg-surface p-6 shadow-card sm:p-8"
            data-testid="category-banner"
          >
            <div className="min-w-0 flex-1">
              <p className="mb-2 flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.22em] text-amber-500">
                <span className="h-px w-8 bg-amber-500" />
                Category
              </p>
              <h2 className="font-display text-2xl font-semibold tracking-tight text-1 sm:text-3xl" data-testid="category-banner-title">
                {activeCategory.name}
              </h2>
              {activeCategory.description && (
                <p className="mt-2 max-w-lg text-sm leading-relaxed text-2">{activeCategory.description}</p>
              )}
              <p className="mt-3 text-[11px] font-bold uppercase tracking-[0.18em] text-3" data-testid="category-banner-count">
                {loading ? "…" : `${total} product${total === 1 ? "" : "s"}`}
              </p>
            </div>
            {activeCategory.image && (
              <div className="hidden h-28 w-28 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-line bg-white sm:flex sm:h-36 sm:w-36">
                <img src={activeCategory.image} alt={activeCategory.name} className="h-full w-full object-cover" />
              </div>
            )}
          </motion.div>
        )}

        <div className="relative mt-8" ref={searchRef}>
          <div className="flex items-center gap-3 rounded-2xl border border-line bg-surface px-5 shadow-card">
            <Search className="h-5 w-5 shrink-0 text-amber-500" />
            <input
              ref={searchInputRef}
              data-testid="shop-search-input"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setShowSuggestions(true); }}
              onFocus={() => setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              placeholder="Search products, packaging, supplies..."
              className="h-14 w-full bg-transparent text-sm text-1 placeholder:text-[var(--pq-text3)] focus:outline-none"
            />
            {search && (
              <button
                data-testid="shop-search-clear"
                onClick={() => setSearch("")}
                className="text-3 transition-colors hoverable-t1"
                aria-label="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <AnimatePresence>
            {showSuggestions && suggestions.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 6 }}
                className="absolute inset-x-0 top-full z-30 mt-2 max-h-[60vh] overflow-y-auto rounded-xl border border-line bg-surface shadow-2xl"
                data-testid="shop-suggestions"
                data-lenis-prevent
              >
                {suggestions.map((s) => (
                  <button
                    key={s.id}
                    data-testid={`suggestion-${s.id}`}
                    onClick={() => { setSearch(s.name); setShowSuggestions(false); }}
                    className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hoverable"
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-md border border-line bg-white p-1">
                      <img src={s.image} alt="" className="max-h-full max-w-full object-contain" loading="lazy" />
                    </span>
                    <span className="flex-1 truncate text-sm text-2">{s.name}</span>
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="shop-category-chips">
          {chipDefs.map((c, i) => (
            <button
              key={c.label}
              data-testid={`chip-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              onClick={() => setFilters((f) => ({ ...f, category: c.category, subcategory: "", flag: c.flag }))}
              className={`rounded-full border px-4 py-2 text-xs font-bold transition-all ${
                activeChip === i
                  ? "border-amber-500 bg-amber-500 text-black"
                  : "border-line text-2 hover:border-amber-500/40 hoverable-t1"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        {activeCategory?.subcategories?.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-2 pl-2" data-testid="shop-subcategory-chips">
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-3">{activeCategory.name} ›</span>
            {activeCategory.subcategories.map((s) => (
              <button
                key={s.id}
                data-testid={`subchip-${s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onClick={() => setFilters((f) => ({ ...f, subcategory: f.subcategory === s.name ? "" : s.name }))}
                className={`rounded-full border px-3.5 py-1.5 text-[11px] font-bold transition-all ${
                  filters.subcategory === s.name
                    ? "border-amber-500 bg-amber-500/15 text-amber-400"
                    : "border-line text-3 hover:border-amber-500/40 hoverable-t1"
                }`}
              >
                {s.name}
              </button>
            ))}
          </div>
        )}

        <div className="mt-8 flex gap-8">
          <aside className="hidden w-60 shrink-0 lg:block">
            <div className="sticky top-24 rounded-2xl border border-line bg-surface p-6 shadow-card">
              <FilterPanel filters={filters} setFilters={setFilters} categories={categories} brands={brands} />
            </div>
          </aside>

          <div className="min-w-0 flex-1">
            <div className="mb-5 flex items-center justify-between gap-3">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-3" data-testid="shop-result-count">
                {loading ? "Loading…" : `${total} products`}
              </p>
              <div className="flex items-center gap-2.5">
                <button
                  data-testid="shop-filters-mobile-btn"
                  onClick={() => setFiltersOpen(true)}
                  className="flex items-center gap-2 rounded-lg border border-line px-3.5 py-2.5 text-xs font-bold text-2 lg:hidden"
                >
                  <SlidersHorizontal className="h-3.5 w-3.5" />
                  Filters
                </button>
                <select
                  data-testid="shop-sort-select"
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                  className="h-10 rounded-lg border border-line bg-surface px-3 text-xs font-bold text-2 focus:border-amber-500/60 focus:outline-none"
                >
                  {SORTS.map(([val, label]) => (
                    <option key={val} value={val}>{label}</option>
                  ))}
                </select>
              </div>
            </div>

            {error && (
              <div className="rounded-2xl border border-red-500/25 bg-red-500/[0.06] p-8 text-center text-sm text-red-400" data-testid="shop-error">
                {error}
              </div>
            )}

            {loading ? (
              <div className="grid grid-cols-2 gap-4 lg:grid-cols-3" data-testid="shop-loading">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="animate-pulse rounded-2xl border border-line bg-surface">
                    <div className="aspect-[4/3] rounded-t-2xl bg-hover" />
                    <div className="space-y-2.5 p-4">
                      <div className="h-3 w-1/3 rounded bg-hover" />
                      <div className="h-4 w-2/3 rounded bg-hover" />
                      <div className="h-8 w-full rounded-lg bg-hover" />
                    </div>
                  </div>
                ))}
              </div>
            ) : products.length === 0 && !error ? (
              <div className="flex flex-col items-center gap-4 rounded-2xl border border-line bg-surface py-20 text-center shadow-card" data-testid="shop-empty">
                <span className="flex h-16 w-16 items-center justify-center rounded-2xl border border-line bg-hover text-amber-500">
                  <PackageSearch className="h-7 w-7" strokeWidth={1.6} />
                </span>
                <h3 className="font-display text-2xl font-semibold tracking-tight text-1">No products found</h3>
                <p className="max-w-xs text-sm text-3">
                  Try searching for boxes, mailers, labels or packaging supplies.
                </p>
                <button
                  data-testid="shop-empty-clear"
                  onClick={() => { setSearch(""); setFilters({ category: "", flag: "", brand: "", min_price: "", max_price: "", availability: "", min_rating: "0" }); }}
                  className="mt-2 rounded-full border border-amber-500/50 px-6 py-3 text-sm font-bold text-amber-400 transition-colors hover:bg-amber-500/10"
                >
                  Clear Search & Filters
                </button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-4 lg:grid-cols-3" data-testid="shop-grid">
                  {products.map((p, i) => (
                    <ProductCard key={p.id} product={p} index={i} />
                  ))}
                </div>
                {page < pages && (
                  <div className="mt-10 text-center">
                    <button
                      data-testid="shop-load-more"
                      onClick={() => fetchProducts(page + 1, true)}
                      disabled={loadingMore}
                      className="inline-flex items-center gap-2 rounded-full border border-strong px-8 py-3.5 text-sm font-bold text-1 transition-colors hover:border-amber-500/50 hover:text-amber-400 disabled:opacity-60"
                    >
                      {loadingMore && <Loader2 className="h-4 w-4 animate-spin" />}
                      Load More Products
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {filtersOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setFiltersOpen(false)}
              className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm lg:hidden"
            />
            <motion.div
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "spring", stiffness: 300, damping: 32 }}
              className="fixed left-0 top-0 z-[75] h-full w-80 max-w-[85vw] overflow-y-auto border-r border-line bg-surface p-6 lg:hidden"
              data-lenis-prevent
              data-testid="shop-filter-drawer"
            >
              <div className="mb-6 flex items-center justify-between">
                <h3 className="font-display text-lg font-semibold">Filters</h3>
                <button onClick={() => setFiltersOpen(false)} className="text-2 hoverable-t1" aria-label="Close filters" data-testid="shop-filter-close">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <FilterPanel filters={filters} setFilters={setFilters} categories={categories} brands={brands} />
              <button
                data-testid="shop-filter-apply"
                onClick={() => setFiltersOpen(false)}
                className="mt-8 w-full rounded-xl bg-amber-500 py-3 text-sm font-bold text-black"
              >
                Apply Filters
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
